import { asPromise } from '@bcf-vanilla-ts-v1-shared/misc/rx-helpers/as-promise';
import type { Observable } from 'rxjs';
import { distinctUntilChanged, map, merge, of, ReplaySubject, Subject } from 'rxjs';
import type { CookiesKeys } from './cookies.keys';
import type { CookiesService } from './cookies.service';

type CookiesContainer = Record<string, string | undefined>;

function extractCookies(cookiesStr: string): Record<string, string> {
  const container: Record<string, string> = {};

  const splitedOuter: string[] = cookiesStr.split(';');
  for (const innerStr of splitedOuter) {
    const splitedInner: string[] = innerStr.split('=');
    container[splitedInner[0].trim()] = splitedInner[1];
  }

  return container;
}

/**
 * @see https://github.com/stevermeister/ngx-cookie-service/blob/master/projects/ngx-cookie-service/src/lib/cookie.service.ts
 */
function setCookie(name: string, value: string, expires?: Date, path?: string, domain?: string): string {
  let cookieString: string = encodeURIComponent(name) + '=' + encodeURIComponent(value) + ';';
  if (expires) {
    cookieString += 'expires=' + expires.toUTCString() + ';';
  }
  cookieString += 'path=' + (path ?? '/') + ';';
  if (domain) {
    cookieString += 'domain=' + domain + ';';
  }
  return cookieString;
}

export class CookiesCSR implements CookiesService {
  private _didUpdated$: Subject<void> = new Subject<void>();
  private _cookies$: ReplaySubject<CookiesContainer> = new ReplaySubject<CookiesContainer>(1);

  private _currentCookiesStr: string = '';

  constructor() {
    this._init();
  }

  public didUpdated(): Observable<void> {
    return merge(of(undefined), this._didUpdated$);
  }

  private _init(): void {
    this._checkCookies();
    setInterval(() => this._checkCookies(), 250);
  }

  private _checkCookies(): void {
    if (this._currentCookiesStr !== document.cookie) {
      this._currentCookiesStr = document.cookie;
      const cookies: Record<string, string> = extractCookies(this._currentCookiesStr);
      this._cookies$.next(cookies);
      this._didUpdated$.next(undefined);
    }
  }

  public async get(key: CookiesKeys): Promise<string | undefined> {
    const cookies: CookiesContainer = await asPromise(this._cookies$);
    return cookies[key];
  }

  public async set(name: CookiesKeys, value: string, expires?: Date, path?: string, domain?: string): Promise<void> {
    document.cookie = setCookie(name, value, expires, path, domain);
    this._checkCookies();
    return new Promise((resolve: (value: void | PromiseLike<void>) => void) => setTimeout(resolve, 1));
  }

  public getThemeState(): Observable<string | undefined> {
    return this._cookies$.pipe(
      map((cookies: CookiesContainer) => cookies['prefers_color_scheme']),
      distinctUntilChanged()
    );
  }
}
